import { Button, Callout } from '@aecom-ai-engineering/react-components';
import { Dialog } from '@ark-ui/react/dialog';
import { Portal } from '@ark-ui/react/portal';
import { useSubmitFeedbackMutation } from '@consigli/hooks';
import { FeedbackType, type Services } from '@consigli/types';
import { Info, X } from 'lucide-react';
import { type FC, type RefObject, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { DialogSelect } from './dialog-select';
import { RichCheckboxMark } from './rich-checkbox-mark';
import { useFeedbackServiceOptions } from './use-feedback-service-options';
import './feedback-dialog.css';

const ICON_STROKE_WIDTH = 1.75;

/** The two cards, in the order drawn. Agent feedback is stored as `SKILL`. */
const KINDS = [
  { type: FeedbackType.GENERAL, label: 'feedback-dialog.general' },
  { type: FeedbackType.SKILL, label: 'feedback-dialog.agent' },
] as const;

type FeedbackKind = (typeof KINDS)[number]['type'];

/** Same card as construction-type-field.tsx: the inset ring keeps it 44px tall. */
const CARD =
  'flex cursor-pointer items-start gap-[var(--ds-space-3)] rounded-[var(--ds-radius-10)] bg-[var(--ds-color-bg-primary)] p-[var(--ds-space-3)] shadow-[inset_0_0_0_1px_var(--ds-color-border-primary)] has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-[var(--ds-color-border-primary)]';

/** The frame's `paragraph/medium`: 16/24 medium at 0.25px. */
const SECTION_LABEL =
  'text-[length:var(--ds-typography-paragraph-regular-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-paragraph-regular-line-height)] tracking-[0.25px] text-[var(--ds-color-text-primary)]';

/** 14/20 regular. */
const SMALL_TEXT =
  'text-[length:var(--ds-typography-paragraph-small-font-size)] leading-[var(--ds-typography-paragraph-small-line-height)]';

/*
 * The frame's text-secondary is #57534E, which is raw-stone-600. The exported
 * --ds-color-text-secondary is #78716C, which the frame uses for placeholders.
 */
const SECONDARY_TEXT = 'text-[var(--ds-color-raw-stone-600)]';

interface FeedbackFormProps {
  contentRef: RefObject<HTMLDivElement | null>;
  onClose: () => void;
}

const FeedbackForm: FC<FeedbackFormProps> = ({ contentRef, onClose }) => {
  const { t } = useTranslation();
  const serviceOptions = useFeedbackServiceOptions();
  const [submitFeedback, { isLoading }] = useSubmitFeedbackMutation();
  const [kind, setKind] = useState<FeedbackKind>(FeedbackType.GENERAL);
  const [service, setService] = useState<Services | null>(null);
  const [message, setMessage] = useState('');

  const typeLabelId = useId();
  const calloutId = useId();
  const messageId = useId();

  const isAgent = kind === FeedbackType.SKILL;
  const canSubmit = message.trim() !== '' && (!isAgent || service !== null) && !isLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      await submitFeedback({
        type: kind,
        ...(isAgent && service !== null ? { service } : {}),
        message: message.trim(),
      }).unwrap();

      toast.success(t('feedback-dialog.success'));
      onClose();
    } catch (error) {
      console.error('Failed to submit feedback', error);
      toast.error(t('feedback-dialog.error'));
    }
  };

  return (
    <form
      className="flex flex-col gap-[var(--ds-space-6)]"
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
    >
      <div className="flex flex-col gap-[var(--ds-space-2)]">
        <div className="flex items-center justify-between gap-[var(--ds-space-4)]">
          <Dialog.Title className="text-[length:var(--ds-typography-heading-4-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-heading-4-line-height)] tracking-[var(--ds-typography-heading-4-letter-spacing)] text-[var(--ds-color-text-primary)]">
            {t('feedback-dialog.title')}
          </Dialog.Title>
          <Dialog.CloseTrigger
            aria-label={t('feedback-dialog.close')}
            className="flex size-[var(--ds-space-6)] shrink-0 cursor-pointer items-center justify-center text-[var(--ds-color-bg-icon-primary)]"
          >
            <X
              aria-hidden="true"
              className="size-[var(--ds-space-6)]"
              strokeWidth={ICON_STROKE_WIDTH}
            />
          </Dialog.CloseTrigger>
        </div>
        <Dialog.Description className={`${SMALL_TEXT} ${SECONDARY_TEXT}`}>
          {t('feedback-dialog.description')}
        </Dialog.Description>
      </div>

      <div className="flex flex-col gap-[var(--ds-space-4)]">
        <div className="flex flex-col gap-[var(--ds-space-2)]">
          <span className={SECTION_LABEL} id={typeLabelId}>
            {t('feedback-dialog.type-label')}
          </span>
          {/* Callout's own gap, radius and 13px type are narrower than the
              frame's; utilities outrank its components layer. */}
          <Callout
            className={`gap-[var(--ds-space-3)] rounded-[var(--ds-radius-8)] ${SMALL_TEXT} [--ds-callout-bg:var(--ds-color-bg-brand-light)] [--ds-callout-fg:var(--ds-color-raw-stone-600)] [--ds-callout-icon:var(--ds-color-raw-stone-600)]`}
            icon={Info}
            id={calloutId}
          >
            {t('feedback-dialog.type-info')}
          </Callout>
        </div>

        {/* Gapless, so a closed agent field holds no 16px open under the
            cards. The gap sits inside the part that collapses. */}
        <div className="flex flex-col">
          {/* Radios under the round mark: the frame draws the Rich Checkbox
              Group, but the user picks exactly one of the two. */}
          <div
            aria-describedby={calloutId}
            aria-labelledby={typeLabelId}
            className="flex flex-wrap gap-[var(--ds-space-2)]"
            role="radiogroup"
          >
            {KINDS.map(({ type, label }) => (
              <label className={CARD} key={type}>
                <input
                  checked={kind === type}
                  className="sr-only"
                  name="feedback-kind"
                  onChange={() => {
                    setKind(type);
                    if (type === FeedbackType.GENERAL) setService(null);
                  }}
                  type="radio"
                  value={type}
                />
                <span className="flex shrink-0 items-center pt-[2.5px]">
                  <RichCheckboxMark checked={kind === type} />
                </span>
                <span className={`${SMALL_TEXT} text-[var(--ds-color-text-primary)]`}>
                  {t(label)}
                </span>
              </label>
            ))}
          </div>

          {/* Mounted while closed, so it can animate out: see
              feedback-dialog.css. */}
          <div className="feedback-agent-field" data-open={isAgent}>
            <div>
              <div className="flex flex-col gap-[var(--ds-space-2)] pt-[var(--ds-space-4)]">
                {/* Seen, not announced: the select names itself with the same text. */}
                <span
                  aria-hidden="true"
                  className={`${SMALL_TEXT} font-[var(--ds-typography-paragraph-paragraph-medium-weight)] text-[var(--ds-color-text-primary)]`}
                >
                  {t('feedback-dialog.agent-label')}
                </span>
                <DialogSelect
                  container={contentRef}
                  label={t('feedback-dialog.agent-label')}
                  onValueChange={(next) => setService(next as Services)}
                  options={serviceOptions}
                  placeholder={t('feedback-dialog.agent-placeholder')}
                  value={service}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-[var(--ds-space-2)]">
          <label className={SECTION_LABEL} htmlFor={messageId}>
            {t('feedback-dialog.message-label')}
          </label>
          {/* No textarea ships in the design system at 2.3.3. Border, radius
              and focus ring follow Input.css. */}
          <textarea
            className={`h-[76px] min-h-[76px] w-full resize-y rounded-[var(--ds-radius-8)] border border-solid border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] p-[var(--ds-space-2)] ${SMALL_TEXT} text-[var(--ds-color-text-primary)] outline-none placeholder:text-[var(--ds-color-text-secondary)] hover:border-[var(--ds-color-border-secondary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ds-color-border-primary)]`}
            id={messageId}
            onChange={(event) => setMessage(event.target.value)}
            placeholder={t('feedback-dialog.message-placeholder')}
            value={message}
          />
        </div>
      </div>

      <div className="flex justify-end gap-[var(--ds-space-2)]">
        <Button onClick={onClose} size="md" type="button" variant="outline">
          {t('feedback-dialog.cancel')}
        </Button>
        <Button disabled={!canSubmit} size="md" type="submit" variant="solid">
          {t(isLoading ? 'feedback-dialog.submitting' : 'feedback-dialog.submit')}
        </Button>
      </div>
    </form>
  );
};

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
}

/*
 * Ark's Dialog rather than the design system's, as in agents-intro-dialog.tsx:
 * that component is 640px, and this frame (Figma 11763:161995 and 11763:161996)
 * is 480px with the actions inside the padded body.
 */
export const FeedbackDialog: FC<FeedbackDialogProps> = ({ open, onClose }) => {
  /* Where the agent menu is portaled: see dialog-select.tsx. */
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <Dialog.Root
      closeOnEscape
      closeOnInteractOutside
      lazyMount
      onOpenChange={({ open: next }) => {
        if (!next) onClose();
      }}
      open={open}
      unmountOnExit
    >
      <Portal>
        <Dialog.Backdrop className="fixed inset-0 z-80 bg-[color-mix(in_srgb,var(--ds-color-raw-black)_40%,transparent)]" />
        {/* m-auto rather than items-center, so a card taller than the viewport
          scrolls from its top instead of overflowing both ways. */}
        <Dialog.Positioner className="fixed inset-0 z-80 flex overflow-y-auto overscroll-contain p-[var(--ds-space-4)]">
          {/* ds-body-font because the content is portaled to body, outside the
            AppShell root that sets the family. */}
          <Dialog.Content
            ref={contentRef}
            className="ds-body-font m-auto w-full max-w-[480px] shrink-0 rounded-[var(--ds-radius-16)] bg-[var(--ds-color-bg-primary)] p-[var(--ds-space-6)]"
          >
            {/* Its own component, so every opening starts from an empty form. */}
            <FeedbackForm contentRef={contentRef} onClose={onClose} />
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
